import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

const FC_TRANSFERS_LIST_PATH = '/warehouse/fc-transfers';

/**
 * Resolves the FC Transfers list URL (including preserved query filters) for back navigation
 * from entity/create/accept flows. Mirrors `usePackagingPOBackHref`.
 *
 * When navigated from the merch all-FC list, pass `fcTransfersListPath` in location state so back
 * returns to that list instead of the facility-scoped transfers page.
 *
 * @param {Object} [options]
 * @param {string} [options.emptyFallback='/warehouse/fc-transfers'] - Path when no filter query is available (e.g. accept flow → inbound list).
 * @returns {string | import('react-router-dom').To}
 */
export function useFCTransfersBackHref(options = {}) {
    const { emptyFallback = FC_TRANSFERS_LIST_PATH } = options;
    const location = useLocation();

    const listPath = useMemo(() => {
        const fromState = location.state?.fcTransfersListPath;
        if (typeof fromState === 'string' && fromState.length > 0) {
            return fromState;
        }
        return FC_TRANSFERS_LIST_PATH;
    }, [location.state]);

    const fcTransfersFilters = useMemo(() => {
        const locationState = location.state ?? {};

        if (typeof locationState.fcTransfersFilters === 'string' && locationState.fcTransfersFilters.length > 0) {
            return locationState.fcTransfersFilters;
        }

        // pushToUrl uses pushState — prefer window over React Router's location.search when both exist.
        if (typeof window !== 'undefined' && window?.location?.search) {
            return window.location.search;
        }

        if (typeof location?.search === 'string' && location.search) {
            return location.search;
        }

        return '';
    }, [location.search, location.state]);

    const backHref = useMemo(() => {
        if (!fcTransfersFilters) {
            if (listPath !== FC_TRANSFERS_LIST_PATH) {
                return listPath;
            }
            return emptyFallback;
        }

        return {
            pathname: listPath,
            search: fcTransfersFilters,
        };
    }, [emptyFallback, fcTransfersFilters, listPath]);

    return backHref;
}
